import axios from 'axios';
import { io, type Socket } from 'socket.io-client';

// Client du chat live Guydeez, en remplacement du widget Missive.
//
// Cote serveur, une conversation EST un ticket de la boite info@guydeez.com :
// les messages du visiteur y arrivent comme des messages entrants, la reponse
// de l'agent part de l'application tickets et revient ici en direct par socket.
// La fenetre ne connait donc que trois choses : un uuid de conversation, un
// jeton, et une liste de messages.

const BASE = process.env.NEXT_PUBLIC_SOCKET || '';

// Le navigateur garde la conversation en cours : recharger la page, ou revenir
// le lendemain, ne doit pas rouvrir un ticket vierge chez les agents.
const STORAGE_KEY = 'guydeez_live_chat';

export type ChatRole = 'visitor' | 'agent';

/**
 * Une piece jointe, vue de la fenetre : un identifiant et de quoi l'annoncer.
 *
 * Pas d'URL, et ce n'est pas un oubli — le serveur n'en donne pas. Le fichier
 * se lit en presentant le jeton de la conversation a une route qui verifie les
 * droits, jamais en suivant un lien qui se transmettrait tout seul.
 */
export interface ChatAttachment {
  id: number;
  filename: string;
  mimeType: string;
  size: number;
  isImage: boolean;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  author: string | null;
  at: string | null;
  attachments?: ChatAttachment[];
}

export interface ChatSession {
  uuid: string;
  token: string;
  visitorFirstName?: string | null;
  visitorLastName?: string | null;
  visitorName?: string | null;
  visitorEmail?: string | null;
}

/** Evenement interne : les CTA de la page demandent l'ouverture de la fenetre. */
export const OPEN_EVENT = 'guydeez:live-chat:open';

// `lang` est accepte et ignore : les appelants venaient de `loadAndOpenMissive`
// qui en avait besoin pour choisir la boite Missive du bon pays. Ici la fenetre
// lit la langue dans le store, mais garder la signature evite de toucher les
// huit CTA qui l'appellent.
export const openLiveChat = (_lang?: string | null) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(OPEN_EVENT));
};

export const readSession = (): ChatSession | null => {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.uuid && parsed?.token ? (parsed as ChatSession) : null;
  } catch {
    // Navigation privee, stockage bloque : le chat doit continuer de
    // fonctionner, simplement sans reprise apres rechargement.
    return null;
  }
};

export const writeSession = (session: ChatSession | null) => {
  if (typeof window === 'undefined') return;
  try {
    if (session) window.localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* stockage indisponible — sans effet sur la conversation en cours */
  }
};

const userToken = () => {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem('access_token');
  } catch {
    return null;
  }
};

/**
 * GTM : le visiteur a engage la conversation.
 *
 * Un evenement par session et par onglet, garde par un drapeau en
 * sessionStorage : ce qui interesse le marketing est le nombre de prospects qui
 * se mettent a parler, pas le nombre de lignes qu'ils tapent ensuite. Le
 * drapeau n'est pose qu'APRES la reponse du serveur — un envoi refuse (plafond
 * anti-abus, reseau coupe) le brulerait, et le vrai premier message ne
 * declencherait plus jamais l'evenement.
 *
 * Le tout sous try/catch : navigation privee, stockage bloque ou GTM pas encore
 * charge (il est en `lazyOnload`) ne doivent pas faire echouer un envoi qui,
 * lui, est bien parti.
 */
const trackFirstMessage = () => {
  if (typeof window === 'undefined') return;
  try {
    if (window.sessionStorage.getItem('gz_chat_msg')) return;
    window.sessionStorage.setItem('gz_chat_msg', '1');
    const w = window as any;
    w.dataLayer = w.dataLayer || [];
    w.dataLayer.push({ event: 'chat_message_sent' });
  } catch {
    /* stockage indisponible — sans effet sur le message, deja envoye */
  }
};

/**
 * Ouvre une conversation et envoie le premier message.
 *
 * L'entete Authorization porte le JWT de session s'il y en a un : le serveur
 * s'en sert pour reconnaitre un visiteur connecte et rattacher la conversation
 * a son compte, sans quoi la reponse ne pourrait pas le suivre d'un onglet a
 * l'autre.
 */
export const startConversation = async (payload: {
  firstName: string;
  lastName: string;
  email: string;
  message: string;
  lang?: string | null;
}): Promise<{ session: ChatSession; messages: ChatMessage[] }> => {
  const token = userToken();
  const { data } = await axios.post(
    `${BASE}/api/chat/conversations`,
    {
      ...payload,
      pageUrl: typeof window !== 'undefined' ? window.location.href : null,
    },
    { headers: token ? { Authorization: `Bearer ${token}` } : {} }
  );

  const session: ChatSession = {
    uuid: data.data.uuid,
    token: data.data.token,
    visitorFirstName: data.data.visitorFirstName,
    visitorLastName: data.data.visitorLastName,
    visitorName: data.data.visitorName,
    visitorEmail: data.data.visitorEmail,
  };
  writeSession(session);
  trackFirstMessage();
  return { session, messages: data.data.messages || [] };
};

/**
 * Envoie un message du visiteur.
 *
 * La page courante part avec, a chaque message : l'agent la voit sous la bulle
 * dans le ticket, et sait de quoi on lui parle sans avoir a le demander. Rien
 * n'en revient dans la reponse du serveur, la fenetre n'en affiche donc jamais
 * rien — c'est voulu, le visiteur n'a pas a voir qu'on note ou il se trouve.
 * Relue a chaque envoi et pas une fois a l'ouverture : la fenetre reste ouverte
 * pendant que le visiteur navigue.
 */
export const sendMessage = async (
  session: ChatSession,
  text: string,
  files: File[] = []
): Promise<ChatMessage> => {
  const pageUrl = typeof window !== 'undefined' ? window.location.href : null;

  // Multipart seulement quand il y a des fichiers : le message texte, qui est
  // l'immense majorite, continue de partir en JSON comme avant.
  let body: FormData | Record<string, unknown> = { text, pageUrl };
  if (files.length) {
    const form = new FormData();
    form.append('text', text);
    if (pageUrl) form.append('pageUrl', pageUrl);
    files.forEach((file) => form.append('files', file));
    body = form;
  }

  const { data } = await axios.post(
    `${BASE}/api/chat/conversations/${session.uuid}/messages`,
    body,
    { headers: { Authorization: `Bearer ${session.token}` } }
  );
  // Le visiteur peut reprendre une conversation ouverte hier : son premier
  // message de la session passe par ici et pas par `startConversation`.
  trackFirstMessage();
  return data.data;
};

export const fetchMessages = async (session: ChatSession): Promise<ChatMessage[]> => {
  const { data } = await axios.get(`${BASE}/api/chat/conversations/${session.uuid}/messages`, {
    headers: { Authorization: `Bearer ${session.token}` },
  });
  return data.data?.messages || [];
};

/**
 * Conversation en cours d'un visiteur connecte, cote serveur.
 *
 * Elle prime sur le stockage local : c'est elle qui fait suivre le fil d'un
 * appareil a l'autre, et qui evite qu'un utilisateur connecte sur un nouveau
 * navigateur reparte de zero alors qu'un agent lui a deja repondu.
 */
export const fetchCurrentConversation = async (): Promise<{
  session: ChatSession;
  messages: ChatMessage[];
} | null> => {
  const token = userToken();
  if (!token) return null;
  try {
    const { data } = await axios.get(`${BASE}/api/chat/conversations/current`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!data?.data) return null;
    const session: ChatSession = {
      uuid: data.data.uuid,
      token: data.data.token,
      visitorFirstName: data.data.visitorFirstName,
      visitorLastName: data.data.visitorLastName,
      visitorName: data.data.visitorName,
      visitorEmail: data.data.visitorEmail,
    };
    writeSession(session);
    return { session, messages: data.data.messages || [] };
  } catch {
    return null;
  }
};

/**
 * Recupere une piece jointe et rend une URL d'objet locale.
 *
 * On telecharge avec le jeton plutot que de poser l'adresse dans un `src` :
 * c'est la contrepartie de ne jamais exposer l'URL S3 — le navigateur ne peut
 * pas porter d'entete sur une image, donc on va chercher les octets nous-memes.
 * L'appelant est responsable du `revokeObjectURL`.
 */
export const fetchAttachment = async (session: ChatSession, attachmentId: number): Promise<string> => {
  const { data } = await axios.get(
    `${BASE}/api/chat/conversations/${session.uuid}/attachments/${attachmentId}`,
    { headers: { Authorization: `Bearer ${session.token}` }, responseType: 'blob' }
  );
  return URL.createObjectURL(data);
};

export const closeConversation = async (session: ChatSession) => {
  try {
    await axios.post(
      `${BASE}/api/chat/conversations/${session.uuid}/close`,
      {},
      { headers: { Authorization: `Bearer ${session.token}` } }
    );
  } catch {
    /* fermer la fenetre ne doit jamais echouer visiblement */
  }
};

/**
 * Branche l'ecoute temps reel de la conversation.
 *
 * On se presente avec le JWT de session quand il y en a un, sinon avec le jeton
 * invite : le serveur accepte les deux, mais seul le premier permet de recevoir
 * la reponse sur les autres onglets et appareils du visiteur. Dans ce cas il
 * faut demander explicitement la salle de la conversation (`livechat:join`), le
 * jeton de session ne la nommant pas.
 */
export const connectLiveChat = (
  session: ChatSession,
  onMessage: (message: ChatMessage) => void,
  onClosed?: () => void
): Socket => {
  const sessionToken = userToken();
  const socket = io(BASE, {
    auth: { token: sessionToken || session.token },
    // WebSocket seul : le polling long ouvre une requete HTTP par battement,
    // et le backend est derriere un load balancer sans sticky sessions.
    transports: ['websocket'],
    reconnection: true,
  });

  socket.on('connect', () => {
    if (sessionToken) socket.emit('livechat:join', { conversationUuid: session.uuid });
  });

  socket.on('livechat:message', (payload: { conversationUuid: string; message: ChatMessage }) => {
    // Un utilisateur connecte recoit AUSSI les evenements de sa salle
    // personnelle : sans ce filtre, une conversation precedente pourrait
    // s'afficher dans la fenetre ouverte.
    if (payload?.conversationUuid !== session.uuid) return;
    if (payload?.message) onMessage(payload.message);
  });

  // Un agent a termine la conversation : la fenetre repart de zero. Meme filtre
  // sur l'uuid, pour la meme raison que ci-dessus.
  socket.on('livechat:closed', (payload: { conversationUuid: string }) => {
    if (payload?.conversationUuid !== session.uuid) return;
    if (onClosed) onClosed();
  });

  return socket;
};
